// src/components/AIAssistant.jsx
import React, { useState, useRef, useEffect } from 'react';
import {
  Box,
  Fab,
  Paper,
  Typography,
  TextField,
  IconButton,
  CircularProgress,
  Avatar,
  Divider,
  Zoom,
  Fade,
  Tooltip,
  Chip,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import CloseIcon from '@mui/icons-material/Close';
import SendIcon from '@mui/icons-material/Send';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import DeleteSweepIcon from '@mui/icons-material/DeleteSweep';
import HistoryEduIcon from '@mui/icons-material/HistoryEdu';
import MedicationIcon from '@mui/icons-material/Medication';
import MonitorHeartIcon from '@mui/icons-material/MonitorHeart';
import axiosInstance from '../axiosInstance';


const initialAssistantMessage = {
  role: 'assistant',
  content:
    '¡Hola! Soy tu asistente IA. Puedo ayudarte en el uso del sistema, a buscar términos médicos e información de patologías y sus tratamientos. Puedes empezar preguntándome lo que desees investigar con solo escribirlo abajo :) Dime, ¿en qué puedo ayudarte hoy?'
};

// Normaliza el nombre del modelo para mostrarlo como Chip
const formatModelSource = (src) => {
  if (!src) return null;
  const s = String(src).trim();
  if (s.includes('m42-health')) return 'Med42-70B';
  if (s.toLowerCase().includes('claude')) {
    if (s.includes('4.5') || s.includes('haiku')) return 'Claude 4.5 Haiku';
  }
  if (s.toLowerCase().includes('llama')) return 'Llama';
  return s;
};

const AIAssistant = () => {
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Estilo reutilizable para los Chips de categorías
  const quickChipSx = {
    borderRadius: '20px',
    backgroundColor: isDarkMode
      ? alpha(theme.palette.primary.main, 0.15)
      : alpha(theme.palette.primary.main, 0.1),
    color: isDarkMode ? theme.palette.common.white : theme.palette.primary.main,
    '& .MuiChip-icon': {
      color: isDarkMode ? theme.palette.common.white : theme.palette.primary.main,
    },
    '&:hover': {
      backgroundColor: isDarkMode
        ? alpha(theme.palette.primary.main, 0.25)
        : alpha(theme.palette.primary.main, 0.2),
    },
  };

  const [isOpen, setIsOpen] = useState(false);
  const [hasShownAfterLogin, setHasShownAfterLogin] = useState(false);

  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');

  const [isLoading, setIsLoading] = useState(false);
  const [isTyping, setIsTyping] = useState(false);
  const [showScrollDown, setShowScrollDown] = useState(false);
  const [loadingHistory, setLoadingHistory] = useState(false);

  const messagesEndRef = useRef(null);
  const chatContainerRef = useRef(null);

  // Detectar login reciente para auto-abrir
  useEffect(() => {
    const justLoggedIn = sessionStorage.getItem('justLoggedIn') === 'true';
    if (justLoggedIn && !hasShownAfterLogin) {
      const timer = setTimeout(() => {
        setIsOpen(true);
        setHasShownAfterLogin(true);
        sessionStorage.removeItem('justLoggedIn');
      }, 1500);
      return () => clearTimeout(timer);
    }
  }, [hasShownAfterLogin]);

  // Cargar historial cuando se abre el chat
  useEffect(() => {
    if (!isOpen) return;
    const controller = new AbortController();
    loadConversationHistory(controller.signal);
    return () => controller.abort();
  }, [isOpen]);

  const parseThrottleWaitSeconds = (err) => {
    const header = err?.response?.headers?.['retry-after'];
    if (header) {
      const s = parseInt(header, 10);
      if (!Number.isNaN(s) && s > 0) return s;
    }
    const detail = err?.response?.data?.detail || '';
    const m = String(detail).match(/(\d+)\s*seg/);
    if (m && m[1]) return parseInt(m[1], 10);
    return 30;
  };

  // Cargar historial de conversación desde el servidor
  const loadConversationHistory = async (signal) => {
    const token = localStorage.getItem('authToken');
    if (!token) {
      setMessages([initialAssistantMessage]);
      return;
    }
    try {
      setLoadingHistory(true);
      const response = await axiosInstance.get('/medical-assistant/history/', { signal });
      if (Array.isArray(response.data) && response.data.length > 0) {
        setMessages(response.data);
      } else {
        setMessages([initialAssistantMessage]);
      }
    } catch (error) {
      if (error?.name === 'CanceledError' || error?.name === 'AbortError' || error?.code === 'ERR_CANCELED') {
        return;
      }
      const status = error?.response?.status;
      if (status === 429) {
        const wait = parseThrottleWaitSeconds(error);
        setMessages([
          {
            role: 'assistant',
            content: `Has alcanzado el límite de solicitudes. Intenta nuevamente en ~${wait} segundos.`
          }
        ]);
        return;
      }
      if (status === 401) {
        setMessages([
          {
            role: 'assistant',
            content:
              'Tu sesión ha expirado o no estás autenticado. Inicia sesión nuevamente para usar el asistente.'
          }
        ]);
        return;
      }
      console.error('Error al cargar el historial de conversación:', error);
      setMessages([initialAssistantMessage]);
    } finally {
      setLoadingHistory(false);
    }
  };

  // Guardar un mensaje en el servidor
  const saveMessageToServer = async (message) => {
    try {
      await axiosInstance.post('/medical-assistant/save-message/', message);
    } catch (error) {
      // Silencioso: no bloquear la UX por fallo de persistencia
      console.error('Error al guardar mensaje en el servidor:', error);
    }
  };

  // Scroll al fondo
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  // Mostrar botón de scroll down
  const handleScroll = () => {
    if (chatContainerRef.current) {
      const { scrollTop, scrollHeight, clientHeight } = chatContainerRef.current;
      const isBottom = scrollHeight - scrollTop - clientHeight < 20;
      setShowScrollDown(!isBottom);
    }
  };

  // Scroll automático al añadir mensajes
  useEffect(() => {
    if (isOpen && !loadingHistory) {
      scrollToBottom();
    }
  }, [messages, isOpen, loadingHistory]);

  // Listener de scroll
  useEffect(() => {
    const chatContainer = chatContainerRef.current;
    if (chatContainer) {
      chatContainer.addEventListener('scroll', handleScroll);
      return () => chatContainer.removeEventListener('scroll', handleScroll);
    }
  }, []);

  const requireTokenOrInform = () => {
    const token = localStorage.getItem('authToken');
    if (!token) {
      const errorMessage = {
        role: 'assistant',
        content:
          'Lo siento, parece que no has iniciado sesión o tu sesión ha expirado. Por favor, inicia sesión nuevamente para usar el asistente.'
      };
      setMessages((prev) => [...prev, errorMessage]);
      return false;
    }
    return true;
  };

  // Enviar mensaje libre
  const handleSendMessage = async () => {
    if (!input.trim()) return;

    if (!requireTokenOrInform()) {
      setInput('');
      return;
    }

    // Añadir mensaje del usuario al estado y construir payload coherente
    const userMessage = { role: 'user', content: input.trim() };
    const nextMessages = [...messages, userMessage];

    setMessages(nextMessages);
    setInput('');
    setIsLoading(true);
    setIsTyping(true);

    // Persistir mensaje del usuario
    await saveMessageToServer(userMessage);

    try {
      const response = await axiosInstance.post(
        '/medical-assistant/',
        { messages: nextMessages },
        { timeout: 60000 } // Aumentado a 60s por modelos más grandes (Med42-70B)
      );

      // Simular escritura
      setTimeout(() => {
        setIsTyping(false);

        if (response.data?.message) {
          const assistantMessage = response.data.message;
          setMessages((prev) => [...prev, assistantMessage]);
          saveMessageToServer(assistantMessage);
        } else if (response.data?.error) {
          const errorMessage = {
            role: 'assistant',
            content: `Lo siento, ocurrió un error: ${response.data.error}`
          };
          setMessages((prev) => [...prev, errorMessage]);
          saveMessageToServer(errorMessage);
        } else {
          const errorMessage = {
            role: 'assistant',
            content: 'Respuesta inválida del servidor'
          };
          setMessages((prev) => [...prev, errorMessage]);
          saveMessageToServer(errorMessage);
        }

        setIsLoading(false);
      }, 800);
    } catch (error) {
      console.error('Error al comunicarse con el asistente médico:', error);
      setIsTyping(false);

      let msg = 'Lo siento, ha ocurrido un error al procesar tu consulta médica.';
      const status = error?.response?.status;

      if (status === 429) {
        const wait = parseThrottleWaitSeconds(error);
        msg = `Has alcanzado el límite de solicitudes. Intenta nuevamente en ~${wait} segundos.`;
      } else if (status === 401) {
        msg = 'Tu sesión ha expirado o no estás autenticado. Inicia sesión nuevamente para usar el asistente.';
      } else if (error.response) {
        msg += ` (Error ${status}: ${error.response.data?.error || 'Error del servidor'})`;
      } else if (error.request) {
        msg += ' No se pudo conectar con el servidor.';
      } else {
        msg += ` ${error.message}`;
      }

      const assistantErrorMessage = { role: 'assistant', content: msg };
      setMessages((prev) => [...prev, assistantErrorMessage]);
      saveMessageToServer(assistantErrorMessage);
      setIsLoading(false);
    }
  };

  // Enviar mensaje al seleccionar categoría
  const handleCategorySelect = async (categoryName) => {
    if (isLoading) return;

    if (!requireTokenOrInform()) return;

    const categoryMessage = { role: 'user', content: categoryName, type: 'category_selection' };
    const uiCategoryMessage = { role: 'user', content: categoryName };

    const nextMessages = [...messages, uiCategoryMessage];

    setMessages(nextMessages);
    setIsLoading(true);
    setIsTyping(true);

    await saveMessageToServer(uiCategoryMessage);

    try {
      const response = await axiosInstance.post(
        '/medical-assistant/',
        { messages: [...messages, categoryMessage] },
        { timeout: 60000 } // 60s también aquí
      );

      setTimeout(() => {
        setIsTyping(false);

        if (response.data?.message) {
          const assistantMessage = response.data.message;
          setMessages((prev) => [...prev, assistantMessage]);
          saveMessageToServer(assistantMessage);
        } else if (response.data?.error) {
          const errorMessage = {
            role: 'assistant',
            content: `Lo siento, ocurrió un error: ${response.data.error}`
          };
          setMessages((prev) => [...prev, errorMessage]);
          saveMessageToServer(errorMessage);
        } else {
          const errorMessage = { role: 'assistant', content: 'Respuesta inválida del servidor' };
          setMessages((prev) => [...prev, errorMessage]);
          saveMessageToServer(errorMessage);
        }

        setIsLoading(false);
      }, 800);
    } catch (error) {
      console.error('Error al procesar la categoría:', error);
      setIsTyping(false);

      let msg = 'Lo siento, ha ocurrido un error al procesar esta categoría.';
      const status = error?.response?.status;

      if (status === 429) {
        const wait = parseThrottleWaitSeconds(error);
        msg = `Has alcanzado el límite de solicitudes. Intenta nuevamente en ~${wait} segundos.`;
      } else if (status === 401) {
        msg = 'Tu sesión ha expirado o no estás autenticado. Inicia sesión nuevamente para usar el asistente.';
      } else if (error.response) {
        msg += ` (Error ${status}: ${error.response.data?.error || 'Error del servidor'})`;
      } else if (error.request) {
        msg += ' No se pudo conectar con el servidor.';
      } else {
        msg += ` ${error.message}`;
      }

      const assistantErrorMessage = { role: 'assistant', content: msg };
      setMessages((prev) => [...prev, assistantErrorMessage]);
      saveMessageToServer(assistantErrorMessage);
      setIsLoading(false);
    }
  };

  // Limpiar conversación
  const handleClearConversation = async () => {
    const token = localStorage.getItem('authToken');
    if (!token) {
      setMessages([initialAssistantMessage]);
      return;
    }
    try {
      await axiosInstance.delete('/medical-assistant/clear/');
      loadConversationHistory(); // recargar
    } catch (error) {
      console.error('Error al limpiar la conversación:', error);
      setMessages([initialAssistantMessage]);
    }
  };

  // Manejar envío con Enter
  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  return (
    <>
      {/* Estilos para la animación de puntos */}
      <style jsx="true">{`
        @keyframes blink {
          0% { opacity: 0.2; }
          20% { opacity: 1; }
          100% { opacity: 0.2; }
        }
        .typing-dot {
          width: 8px;
          height: 8px;
          border-radius: 50%;
          background-color: ${isDarkMode ? '#aaa' : '#777'};
          display: inline-block;
          margin: 0 1px;
          animation: blink 1.4s infinite both;
        }
        .typing-dot:nth-of-type(2) { animation-delay: 0.2s; }
        .typing-dot:nth-of-type(3) { animation-delay: 0.4s; }
      `}</style>

      {/* Botón flotante */}
      <Zoom in={true}>
        <Fab
          color="primary"
          aria-label="medical-assistant"
          onClick={() => setIsOpen(!isOpen)}
          sx={{
            position: 'fixed',
            bottom: `calc(24px + env(safe-area-inset-bottom, 0px))`,
            right: `calc(24px + env(safe-area-inset-right, 0px))`,
            boxShadow: theme.shadows[6],
            zIndex: 1000,
            transition: 'all 0.3s ease',
            borderRadius: '16px',
            '&:hover': {
              transform: 'scale(1.05)',
              boxShadow: theme.shadows[8],
            }
          }}
        >
          {isOpen ? <CloseIcon /> : <SmartToyIcon />}
        </Fab>
      </Zoom>

      {/* Ventana del chat */}
      <Fade in={isOpen}>
        <Paper
          elevation={6}
          sx={{
            display: isOpen ? 'flex' : 'none',
            flexDirection: 'column',
            position: 'fixed',
            bottom: isMobile ? 0 : `calc(90px + env(safe-area-inset-bottom, 0px))`,
            right: isMobile ? 0 : `calc(24px + env(safe-area-inset-right, 0px))`,
            left: isMobile ? 0 : 'auto',
            width: isMobile ? '100vw' : 360,
            height: isMobile ? '80dvh' : 520,
            maxHeight: isMobile ? '90dvh' : 'calc(100vh - 120px)',
            borderRadius: isMobile ? '18px 18px 0 0' : '28px',
            overflow: 'hidden',
            zIndex: 1000,
            boxShadow: theme.shadows[12],
          }}
        >
          {/* Header */}
          <Box
            sx={{
              p: 2,
              pt: isMobile ? `calc(12px + env(safe-area-inset-top, 0px))` : 2,
              backgroundColor: 'primary.main',
              color: 'primary.contrastText',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Avatar
                sx={{
                  bgcolor: alpha(theme.palette.primary.dark, 0.8),
                  mr: 1.5,
                  width: 40,
                  height: 40
                }}
              >
                <SmartToyIcon />
              </Avatar>
              <Typography variant="h6" fontWeight="medium">NeoMed AI</Typography>
            </Box>
            <Box>
              <Tooltip title="Limpiar conversación">
                <IconButton
                  color="inherit"
                  onClick={handleClearConversation}
                  size="small"
                  sx={{ mr: 1 }}
                >
                  <DeleteSweepIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <IconButton color="inherit" onClick={() => setIsOpen(false)} size="small">
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>
          </Box>

          {/* Mensajes */}
          <Box
            ref={chatContainerRef}
            sx={{
              flex: 1,
              p: 2,
              pb: `calc(2px + env(safe-area-inset-bottom, 0px))`,
              overflowY: 'auto',
              display: 'flex',
              flexDirection: 'column',
              gap: 1.5,
              bgcolor: isDarkMode ? 'background.default' : alpha(theme.palette.primary.main, 0.04),
              position: 'relative',
              '&::-webkit-scrollbar': { width: '0.4em', backgroundColor: 'transparent' },
              '&::-webkit-scrollbar-thumb': {
                backgroundColor: 'rgba(0,0,0,0.1)',
                borderRadius: '4px',
                '&:hover': { backgroundColor: 'rgba(0,0,0,0.2)' },
              },
              scrollbarWidth: 'thin',
              scrollbarColor: 'rgba(0,0,0,0.1) transparent',
            }}
          >
            {loadingHistory ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                <CircularProgress size={40} />
              </Box>
            ) : (
              <>
                {messages.map((msg, index) => {
                  const modelChip = msg.role !== 'user' ? formatModelSource(msg.source) : null;
                  return (
                    <Box
                      key={index}
                      sx={{
                        alignSelf: msg.role === 'user' ? 'flex-end' : 'flex-start',
                        maxWidth: isMobile ? '92%' : '80%',
                      }}
                    >
                      <Paper
                        elevation={0}
                        sx={{
                          p: 1.5,
                          borderRadius: '20px',
                          backgroundColor:
                            msg.role === 'user'
                              ? isDarkMode
                                ? alpha(theme.palette.primary.main, 0.7)
                                : alpha(theme.palette.primary.main, 0.1)
                              : isDarkMode
                                ? alpha(theme.palette.background.paper, 0.8)
                                : '#ffffff',
                          color:
                            msg.role === 'user' && isDarkMode
                              ? theme.palette.primary.contrastText
                              : msg.role === 'user'
                                ? theme.palette.primary.main
                                : 'text.primary',
                          boxShadow:
                            msg.role === 'user'
                              ? 'none'
                              : isDarkMode
                                ? '0 1px 3px 0 rgba(0,0,0,0.3)'
                                : '0 1px 3px 0 rgba(0,0,0,0.1)',
                        }}
                      >
                        <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                          {msg.content}
                        </Typography>

                        {/* Chip con el modelo empleado (si viene del backend) */}
                        {modelChip && (
                          <Box sx={{ mt: 0.75, display: 'flex', gap: 0.5 }}>
                            <Chip
                              size="small"
                              label={modelChip}
                              variant="outlined"
                              sx={{
                                height: 22,
                                borderRadius: '12px',
                                color: isDarkMode ? theme.palette.common.white : 'text.secondary',
                                borderColor: isDarkMode
                                  ? alpha('#fff', 0.3)
                                  : alpha(theme.palette.text.primary, 0.2),
                              }}
                            />
                          </Box>
                        )}
                      </Paper>
                    </Box>
                  );
                })}

                {/* Indicador de escritura */}
                {isTyping && (
                  <Box sx={{ alignSelf: 'flex-start', maxWidth: isMobile ? '92%' : '80%' }}>
                    <Paper
                      elevation={0}
                      sx={{
                        p: 1.5,
                        borderRadius: '20px',
                        backgroundColor: isDarkMode ? alpha(theme.palette.background.paper, 0.8) : '#ffffff',
                        boxShadow: isDarkMode ? '0 1px 3px 0 rgba(0,0,0,0.3)' : '0 1px 3px 0 rgba(0,0,0,0.1)',
                      }}
                    >
                      <Box sx={{ display: 'flex', gap: 0.5 }}>
                        <span className="typing-dot"></span>
                        <span className="typing-dot"></span>
                        <span className="typing-dot"></span>
                      </Box>
                    </Paper>
                  </Box>
                )}
              </>
            )}

            <div ref={messagesEndRef} />

            {/* Botón scroll down */}
            {showScrollDown && (
              <Zoom in={showScrollDown}>
                <Fab
                  size="small"
                  color="primary"
                  aria-label="scroll down"
                  onClick={scrollToBottom}
                  sx={{
                    position: 'absolute',
                    bottom: 16,
                    right: 16,
                    boxShadow: theme.shadows[4],
                    borderRadius: '12px',
                  }}
                >
                  <KeyboardArrowDownIcon />
                </Fab>
              </Zoom>
            )}
          </Box>

          <Divider />

          {/* Categorías rápidas */}
          <Box
            sx={{
              p: 1,
              display: 'flex',
              flexWrap: 'wrap',
              gap: 1,
              justifyContent: 'center',
              backgroundColor: 'background.paper'
            }}
          >
            <Chip
              icon={<HistoryEduIcon />}
              label="Historia clínica"
              size="small"
              onClick={() => handleCategorySelect('Historia clínica')}
              clickable
              variant="filled"
              color="primary"
              sx={quickChipSx}
            />
            <Chip
              icon={<MonitorHeartIcon />}
              label="Diagnósticos"
              size="small"
              onClick={() => handleCategorySelect('Diagnósticos')}
              clickable
              variant="filled"
              color="primary"
              sx={quickChipSx}
            />
            <Chip
              icon={<MedicationIcon />}
              label="Medicamentos"
              size="small"
              onClick={() => handleCategorySelect('Medicamentos')}
              clickable
              variant="filled"
              color="primary"
              sx={quickChipSx}
            />
          </Box>

          {/* Input */}
          <Box
            sx={{
              p: 2,
              pb: `calc(8px + env(safe-area-inset-bottom, 0px))`,
              display: 'flex',
              alignItems: 'center',
              backgroundColor: 'background.paper',
            }}
          >
            <TextField
              fullWidth
              variant="outlined"
              placeholder="Escribe tu consulta médica..."
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyPress={handleKeyPress}
              multiline
              maxRows={3}
              disabled={isLoading || loadingHistory}
              size="small"
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '20px',
                  pr: 1,
                }
              }}
              InputProps={{
                endAdornment: (
                  <IconButton
                    color="primary"
                    onClick={handleSendMessage}
                    disabled={!input.trim() || isLoading || loadingHistory}
                    edge="end"
                  >
                    {isLoading ? <CircularProgress size={24} /> : <SendIcon />}
                  </IconButton>
                ),
              }}
            />
          </Box>
        </Paper>
      </Fade>
    </>
  );
};

export default AIAssistant;
